/**
 * Shared mixin for invoice products logic
 * Used by CreateInvoiceRecord and EditProducts components
 */
export default {
    data() {
        return {
            showDiscountModal: false,
            currentProductPrice: 0,
            currentProductDiscounts: [],
            currentProductRow: null,
            productDiscounts: {}
        }
    },

    computed: {
        subTotal() {
            if (!_.has(this, 'localRecord.products') && !_.has(this, 'record.products')) return false;
            
            const products = this.localRecord?.products || this.record?.products || [];
            let price = 0;
            
            _.each(products, (product) => {
                const isBusinessCustomer = this.record && _.has(this.record, 'customer') && this.record.customer && this.record.customer.type == 'business';
                const discount = this.getProductDiscountAmount(product);

                if (product.price && product.btw) {
                    if (isBusinessCustomer) {
                        const basePrice = parseFloat(product.price) * product.amount;
                        price += basePrice - discount;
                    } else {
                        const productTotal = (parseFloat(product.price) / (1 + Number(product.btw) / 100)) * product.amount;
                        price += productTotal - (discount / (1 + Number(product.btw) / 100));
                    }
                }
            });

            return price;
        },

        btwTotal() {
            if (!_.has(this, 'localRecord.products') && !_.has(this, 'record.products')) return false;
            
            const products = this.localRecord?.products || this.record?.products || [];
            let price = 0;

            _.each(products, (product) => {
                const isBusinessCustomer = this.record && _.has(this.record, 'customer') && this.record.customer && this.record.customer.type == 'business';
                const discount = this.getProductDiscountAmount(product);

                if (isBusinessCustomer) {
                    const basePrice = parseFloat(product.price) * product.amount;
                    const discountedBase = basePrice - discount;
                    price += (discountedBase * product.btw / 100);
                } else {
                    const totalPrice = parseFloat(product.price) * product.amount;
                    const discountedPrice = totalPrice - discount;
                    price += discountedPrice - (discountedPrice / (1 + Number(product.btw) / 100));
                }
            });

            return price;
        },

        totalPrice() {
            if (!_.has(this, 'localRecord.products') && !_.has(this, 'record.products')) return false;
            
            const products = this.localRecord?.products || this.record?.products || [];
            let price = 0;

            _.each(products, (product) => {
                const isBusinessCustomer = this.record && _.has(this.record, 'customer') && this.record.customer && this.record.customer.type == 'business';
                const discount = this.getProductDiscountAmount(product);

                if (isBusinessCustomer) {
                    const basePrice = parseFloat(product.price) * product.amount;
                    const discountedBase = basePrice - discount;
                    const btwAmount = discountedBase * product.btw / 100;
                    price += discountedBase + btwAmount + (parseFloat(product.deposit) * product.amount);
                } else {
                    price += (parseFloat(product.price) * product.amount) + (parseFloat(product.deposit) * product.amount) - discount;
                }
            });

            return price;
        },

        totalDiscountAmount() {
            const products = this.localRecord?.products || this.record?.products || [];
            let total = 0;
            products.forEach(product => {
                total += this.getProductDiscountAmount(product);
            });
            return total;
        }
    },

    methods: {
        addProduct() {
            const products = this.localRecord?.products || this.record?.products || [];
            products.push({
                id: Math.floor(100000000 + Math.random() * 900000000),
                name: '',
                price: 0,
                deposit: 0,
                amount: 1,
                btw: 21,
                type: 'product',
                contract: null,
                once: 1,
                product: null,
                isNew: true
            });
            this.$forceUpdate();
        },

        removeProduct(productId) {
            const products = this.localRecord?.products || this.record?.products;
            if (!products) return;

            // Check if this will be the last product
            if (products.length === 1) {
                // Remove all products
                if (this.localRecord) {
                    this.localRecord.products = [];
                } else {
                    this.record.products = [];
                }
                // Add a new empty product
                this.addProduct();
            } else {
                // Remove the specific product
                const index = products.findIndex(p => p.id === productId);
                if (index !== -1) {
                    products.splice(index, 1);
                    // Also remove discount for this product
                    Vue.delete(this.productDiscounts, productId);
                }
            }
            this.$forceUpdate();
        },

        openDiscountModal(product) {
            this.currentProductRow = product;
            this.currentProductPrice = parseFloat(product.price) * (product.amount || 1);
            this.currentProductDiscounts = this.productDiscounts[product.id] || [];
            this.showDiscountModal = true;
        },

        applyDiscount(discountData) {
            if (this.currentProductRow) {
                Vue.set(this.productDiscounts, this.currentProductRow.id, [discountData]);
                // Store discount fields directly on product for backend
                this.currentProductRow.discount_type = discountData.type;
                this.currentProductRow.discount_value = discountData.value;
                this.currentProductRow.discount_description = discountData.description;
                this.$forceUpdate();
            }
            this.showDiscountModal = false;
        },

        removeDiscount() {
            if (this.currentProductRow) {
                Vue.delete(this.productDiscounts, this.currentProductRow.id);
                // Restore original price if available
                if (this.currentProductRow.org_price) {
                    this.currentProductRow.price = this.currentProductRow.org_price;
                }
                // Remove discount fields from the product
                this.currentProductRow.discount_type = null;
                this.currentProductRow.discount_value = null;
                this.currentProductRow.discount_description = null;
                this.currentProductRow.org_price = null;
                this.$forceUpdate();
            }
            this.showDiscountModal = false;
        },

        hasDiscount(productId) {
            const hasInMemory = this.productDiscounts[productId] && this.productDiscounts[productId].length > 0 && this.productDiscounts[productId][0].value > 0;
            
            // Also check if product has discount fields set (for loaded data)
            const products = this.localRecord?.products || this.record?.products || [];
            const product = products.find(p => p.id === productId);
            const hasInProduct = product && product.discount_value && product.discount_value > 0;
            
            return hasInMemory || hasInProduct;
        },

        getDiscountDisplay(product) {
            const discounts = this.productDiscounts[product.id];
            
            // Check in-memory discount first
            if (discounts && discounts.length > 0 && discounts[0].value > 0) {
                const discount = discounts[0];
                if (discount.type === 'percentage') {
                    return `${discount.value}%`;
                } else {
                    return `€${discount.value}`;
                }
            }
            
            // Check product fields for loaded discounts
            if (product.discount_value && product.discount_value > 0) {
                if (product.discount_type === 'percentage') {
                    return `${product.discount_value}%`;
                } else {
                    return `€${product.discount_value}`;
                }
            }
            
            return 'Toevoegen';
        },

        getProductDiscountAmount(product) {
            const discounts = this.productDiscounts[product.id];
            let discount = null;
            
            // Check in-memory discount first
            if (discounts && discounts.length > 0 && discounts[0].value > 0) {
                discount = discounts[0];
            } 
            // Check product fields for loaded discounts
            else if (product.discount_value && product.discount_value > 0) {
                discount = {
                    type: product.discount_type,
                    value: product.discount_value
                };
            }
            
            if (!discount) {
                return 0;
            }

            // Use org_price if available (original price before discount), otherwise use current price
            const price = parseFloat(product.org_price || product.price) || 0;
            const amount = product.amount || 1;

            if (discount.type === 'percentage') {
                return (price * amount) * (discount.value / 100);
            } else {
                return Math.min(discount.value * amount, price * amount);
            }
        },

        initializeProductDiscounts() {
            // Initialize discounts from products that already have discount data
            const products = this.localRecord?.products || this.record?.products || [];
            products.forEach(product => {
                if (product.discount_type && product.discount_value) {
                    Vue.set(this.productDiscounts, product.id, [{
                        type: product.discount_type,
                        value: product.discount_value,
                        description: product.discount_description || ''
                    }]);
                }
            });
        }
    }
}
